import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { AppState } from '../../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import * as R from 'ramda';
import { Rater } from './models';
import { Map } from '../../../language/models';


const KEY_ACTION = ActionType.SetApproverRequest;
const ACTIONS    = [
  ActionType.SetApproverRequest,
  ActionType.SetApproverResponse,
  ActionType.SetApproverError,
];


type Payload = Rater;


type Meta = {
  projectId: number;
  raterId: number;
};


export const setApproverAction = (
  projectId: number,
  participantId: number,
  raterId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SetApprover,
    headers:  { 'content-type': 'application/json' },
    body: (state: AppState) => JSON.stringify({
      projectId:     projectId,
      participantId: participantId,
      raterId:       raterId,
      pageLanguageId: state.locale.lang.languageId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        raterId:   raterId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${raterId}`),
  },
);


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}-${action.meta.raterId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  // There should only ever be one approver set at a given time, but given the state of the current admin module
  // leaves open the possibility for an unexpected edge case where have more than one.
  // Doesn't seem to be an easy way around having to check the whole collection.

  const allRaters        = state.projectRaters?.[action.meta.projectId] || {};
  const raterIdsToUpdate = R.filter(key => allRaters[key].approvalStatus === 1, Object.keys(allRaters));
  const ratersToUpdate   = R.pick(raterIdsToUpdate, allRaters) as Map<Rater>;
  const updatedRaters    = Object.values(ratersToUpdate).reduce(
    (map, rater) => R.assoc(`${rater.raterId}`, R.mergeRight(rater, { approvalStatus: 2 }), map),
    {},
  );

  const withUpdatedRaters = R.mergeRight(allRaters, updatedRaters);

  const withNewApprover = R.assoc(`${payload!.raterId}`, payload!, withUpdatedRaters);

  return R.assocPath(
    ['projectRaters', `${action.meta.projectId}`],
    withNewApprover,
    successState,
  );
};


export const setApproverReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
